"use client"

import { useState, useEffect, useCallback, useRef, useId } from 'react'
import { X, Mail, CheckCircle, AlertCircle, ArrowRight, CalendarCheck2 } from 'lucide-react'
import { analytics, trackEvent } from '@/lib/analytics'
import { detectEmailTypo, type EmailSuggestion } from '@/lib/email-typo-detection'
import { markNewsletterSubscribed } from '@/lib/usage-tracker'
import { topicFromCategory, strictTopicFromCategory } from '@/lib/newsletter-topic'

interface NewsletterSignupModalProps {
  isOpen: boolean
  onClose: () => void
  toolName?: string
  usageCount?: number // For analytics: which usage triggered this modal
  // When the host tool knows the user's grass/ZIP, pass them so the signup
  // delivers the real 12-month care plan (not the generic newsletter welcome).
  // Nullable to match how each tool already types these for ResultEmailCaptureCard.
  grassType?: string | null
  zipCode?: string | null
  // Content pages (blog/guide/compare) pass the post category or grass name so
  // the modal names the reader's topic — same specificity rule as the inline
  // post-read CTA. Ignored for calculator sources, which have their own copy.
  category?: string | null
}

const CONTENT_MODAL_SOURCES = new Set(['blog', 'guide', 'compare'])
const MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

// Glass surface treatment from the Claude Design "Email Calendar CTA" spec.
// Inline styles (not Tailwind arbitrary classes) because the site CSS is a
// precompiled string — see project_tailwind_v4_new_arbitrary_jit_gap.
const glassCardStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.90) 0%, rgba(255,255,255,0.80) 100%)',
  backdropFilter: 'blur(16px) saturate(140%)',
  WebkitBackdropFilter: 'blur(16px) saturate(140%)',
  border: '1px solid rgba(255,255,255,0.65)',
  boxShadow: '0 12px 40px rgba(22,163,74,0.10), inset 0 1px 0 rgba(255,255,255,0.85)',
}

const glassIconTileStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(240,253,244,0.75) 100%)',
  border: '1px solid rgba(255,255,255,0.8)',
  boxShadow: '0 6px 16px rgba(22,163,74,0.14), inset 0 1px 0 rgba(255,255,255,0.9)',
}

const gradientCtaStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, rgba(22,163,74,0.92) 0%, rgba(5,150,105,0.92) 100%)',
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: '0 10px 24px -6px rgba(22,163,74,0.45), inset 0 1px 0 rgba(255,255,255,0.35)',
}

function normalizeModalSource(toolName: string): string {
  return toolName.toLowerCase().trim().replace(/\s+/g, '_')
}

function getSubscribeContext(toolName: string): string {
  const source = normalizeModalSource(toolName)
  return CONTENT_MODAL_SOURCES.has(source) ? `content_${source}` : `calculator_${source}`
}

function shouldShowContextBridge(toolName: string): boolean {
  const source = normalizeModalSource(toolName)
  return source !== 'calculator' && !CONTENT_MODAL_SOURCES.has(source)
}

function getContextBridgeLabel(toolName: string): string {
  if (normalizeModalSource(toolName) === 'lead-magnet-2026-guide') {
    return "2026 Lawn Products Buyer's Guide"
  }
  return toolName
}

interface ModalContent {
  title: string
  subtitle: string
  buttonText: string
  benefits: React.ReactNode[]
  successTitle: string
  successBody: string
  /** Step-1 yes/no ask. Branches without one fall back to the generic ask. */
  microAsk?: string
}

function getModalContent(toolName: string, topic?: string | null): ModalContent {
  const normalizedToolName = toolName.toLowerCase()

  // Content pages (blog/guide/compare): lead with the 12-month care calendar —
  // the site's proven capture asset — instead of a generic newsletter pitch.
  // Day-one blog-modal data (2026-07): the generic "monthly tips" ask converted
  // 0.7% with ~70% explicit dismissals; the calendar-led inline CTA is the
  // best-performing ask on the same page. Topic-aware when the category is known.
  if (CONTENT_MODAL_SOURCES.has(normalizeModalSource(toolName))) {
    // Guide pages name the site's most specific asset: the grass playbook the
    // reader is already inside. Specificity rule (growth-design): name the
    // thing, not the category.
    if (normalizeModalSource(toolName) === 'guide' && topic) {
      return {
        title: `Get the complete ${topic} playbook`,
        subtitle: `Your 12-month ${topic} care calendar, emailed free`,
        buttonText: `Email me the ${topic} playbook`,
        microAsk: `Want the full ${topic} playbook and calendar by email?`,
        benefits: [
          <>What to do <strong>each month</strong>, tuned to {topic} lawns</>,
          <><strong>Seasonal windows</strong> for feeding, seed, and weed control</>,
          <><strong>Product picks</strong> we actually use, with plain-English notes</>
        ],
        successTitle: `Your ${topic} playbook is on its way!`,
        successBody: 'It lands in your inbox in about 2 minutes. Check your promotions tab if you do not see it right away.'
      }
    }
    return {
      title: topic
        ? `Get your free ${topic} care calendar`
        : 'Get your free 12-month lawn care calendar',
      subtitle: '12 months of seasonally-timed tasks for your lawn',
      buttonText: 'Email me the calendar',
      microAsk: 'One email a month, timed to your season. Want the calendar?',
      benefits: [
        <>What to do <strong>each month</strong>, timed to your season</>,
        <><strong>Seasonal windows</strong> for fertilizer, seed, and weed control</>,
        <><strong>Product picks</strong> we actually use, with plain-English notes</>
      ],
      successTitle: topic
        ? `Your ${topic} care calendar is on its way!`
        : 'Your care calendar is on its way!',
      successBody: 'It lands in your inbox in about 2 minutes. Check your promotions tab if you do not see it right away.'
    }
  }

  if (normalizedToolName === 'lead-magnet-2026-guide') {
    return {
      title: 'Free 2026 Lawn Products Guide',
      subtitle: '35 picks worth your money + 12 to skip. Sent straight to your inbox.',
      buttonText: 'Send me the guide',
      benefits: [
        <>Real picks across <strong>14 product categories</strong></>,
        <>The <strong>12 products to skip</strong> (and why)</>,
        <>Plus the <strong>12-month calendar</strong> as a bonus</>
      ],
      successTitle: 'Your guide is on its way!',
      successBody: 'Check your inbox for the 2026 products guide plus your 12-month care calendar.'
    }
  }

  if (normalizedToolName.includes('soil test')) {
    return {
      title: 'Keep your soil plan handy',
      subtitle: "Monthly lawn timing tips, plus reminders to review your soil numbers before the next season",
      buttonText: 'Send the timing tips',
      benefits: [
        <>Seasonal <strong>soil and fertilizer timing</strong> guidance</>,
        <>Plain-English reminders for <strong>what to check before applying</strong></>,
        <><strong>Product picks</strong> we actually use, when they fit the season</>
      ],
      successTitle: 'Your soil plan is covered!',
      successBody: "Soil and fertilizer timing notes are on the way, with reminders to recheck your numbers before each season."
    }
  }

  if (normalizedToolName.includes('soil temperature')) {
    return {
      title: "Don't seed too early next spring",
      subtitle: "Get monthly soil-temperature timing notes before the busy windows",
      buttonText: 'Send the timing tips',
      benefits: [
        <>How to use <strong>55°F and 65°F soil temp signals</strong></>,
        <>What to check this month, <strong>before the window closes</strong></>,
        <><strong>Product picks</strong> we actually use, when conditions fit</>
      ],
      successTitle: "You won't miss the window!",
      successBody: "Soil-temperature timing notes are on the way. We'll flag the 55°F and 65°F windows before they open."
    }
  }

  if (normalizedToolName.includes('fertilizer')) {
    return {
      title: 'Time fertilizer to your season, not the calendar',
      subtitle: 'Monthly timing notes for feeding, watering, and mowing windows',
      buttonText: 'Send the timing tips',
      benefits: [
        <>When <strong>cool-season vs warm-season grass</strong> is usually ready to feed</>,
        <><strong>Watering notes</strong> for the week after each application</>,
        <><strong>Product picks</strong> we actually use, without guessing your exact soil test</>
      ],
      successTitle: 'Your feeding schedule is set!',
      successBody: "Fertilizer timing notes are on the way, with the watering and mowing windows around each application."
    }
  }

  if (normalizedToolName.includes('seeding')) {
    return {
      title: "Don't miss your seeding window",
      subtitle: "Monthly timing notes so you know what to watch before you seed",
      buttonText: 'Send the timing tips',
      benefits: [
        <>Heads-up on the <strong>conditions to verify</strong> before seeding</>,
        <>Watering and mowing <strong>prep</strong> the week before you seed</>,
        <><strong>Product picks</strong> we actually use, when they fit your project</>
      ],
      successTitle: "You'll catch your seeding window!",
      successBody: "Seeding-window reminders are on the way, with the conditions and prep to check before you sow."
    }
  }

  if (normalizedToolName.includes('watering')) {
    return {
      title: 'Right amount, right week',
      subtitle: 'Monthly watering notes for heat, drought, rain, and seasonal changes',
      buttonText: 'Send the timing tips',
      benefits: [
        <>Seasonal watering <strong>patterns to watch</strong> as conditions change</>,
        <>What to check during <strong>dry spells and heat waves</strong></>,
        <><strong>Product picks</strong> we actually use, when they solve a real problem</>
      ],
      successTitle: "You're dialed in on watering!",
      successBody: "Watering notes are on the way for heat, drought, rain, and the seasonal changes in between."
    }
  }

  // Lawn-size page. By the time this passive fallback fires, the inline result
  // card has been suppressed for anyone who measured — so this copy speaks to
  // the visitor who did NOT measure and is about to leave. Promise only what a
  // grass-less signup actually sends: monthly timing notes. No calendar claim.
  if (normalizedToolName.includes('lawn size')) {
    return {
      title: 'Know what your lawn needs this month',
      subtitle: 'Monthly timing notes for feeding, seeding, and weed windows, whatever your lawn size',
      buttonText: 'Send the timing tips',
      microAsk: 'Want monthly lawn timing notes by email?',
      benefits: [
        <>What to check <strong>this month</strong> before you buy or apply</>,
        <><strong>Seasonal windows</strong> for fertilizer, seed, and weed control</>,
        <><strong>Product picks</strong> we actually use, with plain-English notes</>
      ],
      successTitle: "You're all set!",
      successBody: 'Monthly lawn timing notes are on the way. You can come back and measure your yard anytime.'
    }
  }

  return {
    title: 'Stay one task ahead of your lawn',
    subtitle: 'Monthly timing tips for grass care, weather windows, and product choices',
    buttonText: 'Send the timing tips',
    benefits: [
      <>What to check <strong>this month</strong> before you apply anything</>,
      <><strong>Seasonal windows</strong> for fertilizer, seed, and weed control</>,
      <><strong>Product picks</strong> we actually use, with plain-English notes</>
    ],
    successTitle: "You're all set!",
    successBody: 'Check your inbox for monthly lawn care timing tips.'
  }
}

export function NewsletterSignupModal({
  isOpen,
  onClose,
  toolName = 'calculator',
  usageCount = 1,
  grassType,
  zipCode,
  category,
}: NewsletterSignupModalProps) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null)
  // Two-step micro-commitment (Zeigarnik): the modal opens on a single yes/no
  // ask; the email field only appears after "Yes". Self-started action lifts
  // completion ~84% over a one-step form (OptinMonster/Wisepops). Reset on close.
  const [confirmedIntent, setConfirmedIntent] = useState(false)

  const headingId = useId()
  const emailInputId = useId()
  const modalRef = useRef<HTMLDivElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const successHeadingRef = useRef<HTMLHeadingElement>(null)
  const modalBodyRef = useRef<HTMLDivElement>(null)
  // Element focused before the modal opened, so we can restore focus on close.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  const handleDismiss = useCallback(() => {
    analytics.modalDismissed('newsletter_signup', toolName)
    setError(null)
    onClose()
  }, [toolName, onClose])

  // Guides pass grass display names — keep them verbatim. Blog/compare pass
  // CMS category labels — keyword-match only, so unmapped taxonomy ("Problem
  // Solving") falls back to the generic calendar copy instead of leaking raw.
  // Derived here rather than below the `!isOpen` early return so the impression
  // event can report which branch of copy the visitor actually saw.
  const topic = normalizeModalSource(toolName) === 'guide'
    ? topicFromCategory(category ?? undefined)
    : strictTopicFromCategory(category ?? undefined)

  // Track when modal is shown (for conversion funnel analysis). The first thing
  // shown is the yes/no step, so the micro-commitment impression fires here too —
  // it's the true top of the two-step funnel (shown → yes → signup).
  useEffect(() => {
    if (isOpen) {
      analytics.newsletterModalShown(toolName, usageCount)
      // Also fire the standard capture-surface impression so the modal joins the
      // site-wide email_capture_surface_shown → email_captured funnel like every
      // other surface (the legacy newsletter_modal_shown stays for dashboard
      // continuity). Source is `${toolName}_modal` verbatim — the same key the
      // signup uses (analytics.newsletterSignup(email, `${toolName}_modal`)) so
      // the shown→captured funnel joins. emailSurfaceFamily folds *_modal → 'modal'.
      // `topic` rides along because getModalContent branches on it. Without it the
      // modal's ~8k impressions cannot be split by offer specificity, which leaves
      // half the passive capture layer untestable.
      analytics.surfaceShown(`${toolName}_modal`, { tool_name: toolName, topic: topic ?? null })
      trackEvent('newsletter_micro_commitment_shown', { tool_name: toolName, topic: topic ?? null })
    }
  }, [isOpen, toolName, usageCount, topic])

  // Handle dismissal and keep keyboard focus within the modal.
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleDismiss()
        return
      }
      if (e.key !== 'Tab' || !modalRef.current) return

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR)
      ).filter((element) => !element.hasAttribute('disabled'))
      if (focusable.length === 0) {
        e.preventDefault()
        modalRef.current.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleDismiss])

  // Focus management: on open, remember the previously-focused element and move
  // focus into the modal. On close, restore focus to where it was. Bounds the
  // a11y gap without a full Dialog-primitive migration.
  useEffect(() => {
    if (!isOpen) return

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    // Defer until the dialog has rendered so the target node exists.
    const raf = requestAnimationFrame(() => {
      const root = modalRef.current
      if (!root) return
      const focusTarget = root.querySelector<HTMLElement>(
        '[data-modal-initial-focus], input, button, a[href], [tabindex]:not([tabindex="-1"])'
      )
      focusTarget?.focus()
    })

    return () => {
      cancelAnimationFrame(raf)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [isOpen])

  // When a step replaces the focused control, move focus to the new task
  // instead of dropping it back to the document body.
  useEffect(() => {
    if (!isOpen) return
    const frame = requestAnimationFrame(() => {
      // `overflow-hidden` elements can still be scrolled programmatically by
      // focus/scrollIntoView. Pin the dialog shell itself; only the body below
      // is allowed to own scroll state.
      if (modalRef.current) modalRef.current.scrollTop = 0
      if (isSuccess) {
        modalBodyRef.current?.scrollTo({ top: 0 })
        successHeadingRef.current?.focus({ preventScroll: true })
      } else if (confirmedIntent) {
        const input = emailInputRef.current
        const body = modalBodyRef.current
        if (!input || !body) return
        body.scrollTo({ top: 0 })
        input.focus({ preventScroll: true })
        const bodyRect = body.getBoundingClientRect()
        const inputRect = input.getBoundingClientRect()
        if (inputRect.bottom > bodyRect.bottom - 12) {
          body.scrollTop += inputRect.bottom - bodyRect.bottom + 12
        }
        if (modalRef.current) modalRef.current.scrollTop = 0
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [confirmedIntent, isOpen, isSuccess])

  // Reset the form whenever the modal closes so a later reopen on the same
  // mounted instance starts fresh. The success frame persists while open (the
  // 3.5s auto-dismiss was removed); without this reset a second open would
  // re-show the stale success state instead of the signup form.
  useEffect(() => {
    if (isOpen) return
    setIsSuccess(false)
    setIsSubmitting(false)
    setEmail('')
    setError(null)
    setSuggestion(null)
    // Reset the micro-commitment step so a reopen starts at the yes/no ask and
    // analytics stay clean (no carried-over confirmed state across opens).
    setConfirmedIntent(false)
  }, [isOpen])

  if (!isOpen) return null
  const content = getModalContent(toolName, topic)

  const handleConfirmIntent = () => {
    // topic rides along so the step-1 yes-rate splits by pitch, same as the
    // shown event (the 1.85% yes-rate bottleneck read).
    trackEvent('newsletter_micro_commitment_yes', { tool_name: toolName, topic: topic ?? null })
    setConfirmedIntent(true)
  }

  // Shared green header used by both form steps. It sits outside the scrolling
  // step body so focus-driven scrolling can never move the title off-screen.
  const header = (
    <div className="relative z-10 flex-none px-5 pb-1 pt-7 sm:px-7">
      <div className="flex items-start gap-4 pr-10">
        <div
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] text-green-600"
          style={glassIconTileStyle}
        >
          <CalendarCheck2 className="w-6 h-6" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 id={headingId} className="text-lg font-bold tracking-tight leading-snug text-green-800">
            {content.title}
          </h2>
          <p className="text-gray-600 text-[15px] leading-snug mt-1.5">
            {content.subtitle}
          </p>
        </div>
      </div>
    </div>
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)

    try {
      // Call real API endpoint to save email to Supabase
      const response = await fetch('/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          context: getSubscribeContext(toolName),
          // Tool pages keep calculator_* context so grass/ZIP can route to the
          // care-plan email; content pages use content_* to stay out of that namespace.
          grassType,
          zipCode,
        })
      })

      const data = await response.json()

      if (!response.ok) {
        // Handle already subscribed gracefully
        if (data.error === 'already_subscribed') {
          markNewsletterSubscribed()
          setIsSuccess(true) // Still show success to user
        } else if (data.error === 'rate_limit_exceeded') {
          setError('Too many requests. Please try again in a few minutes.')
          setIsSubmitting(false)
          return
        } else {
          setError(data.error || 'Something went wrong. Please try again.')
          setIsSubmitting(false)
          return
        }
      } else {
        // Track successful newsletter signup to analytics. `topic` joins the
        // capture to the impression's pitch so per-pitch conversion is readable
        // (the named-vs-generic lead magnet read, 2026-08-27).
        analytics.newsletterSignup(email, `${toolName}_modal`, topic)
        setIsSuccess(true)
      }

      // No auto-dismiss on success. The post-submit screen is the peak moment
      // and carries the account-creation CTA — leave it up until the user
      // chooses to act or dismiss (X / Maybe later / ESC). The submit button
      // stays in its spinner state until the success frame replaces it.
    } catch (error) {
      console.error('Newsletter signup failed:', error)
      setError('Network error. Please check your connection and try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div data-calc-ignore="" className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs motion-safe:animate-in motion-safe:fade-in duration-200">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className="relative flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-2xl motion-safe:animate-in motion-safe:zoom-in-95 duration-200"
        style={glassCardStyle}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(134,239,172,0.45) 0%, rgba(134,239,172,0) 70%)',
            filter: 'blur(12px)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(251,191,36,0.30) 0%, rgba(251,191,36,0) 70%)',
            filter: 'blur(12px)',
          }}
        />
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-3.5 top-3.5 z-30 flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition hover:text-gray-700 active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.72)',
            border: '1px solid rgba(255,255,255,0.82)',
          }}
          aria-label="Close"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
        {isSuccess ? (
          // Success state — NGL peak frame: success + specific summary +
          // social proof + value-forward CTA into the conversion triad.
          <div ref={modalBodyRef} data-modal-scroll-body className="relative min-h-0 overflow-y-auto overscroll-contain px-6 py-8 text-center sm:px-8 motion-safe:animate-in motion-safe:fade-in-0 duration-200">
            <div
              className="w-14 h-14 rounded-[16px] flex items-center justify-center mx-auto mb-4 text-green-600 motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:fade-in-0 duration-300"
              style={glassIconTileStyle}
            >
              <CheckCircle className="w-7 h-7" />
            </div>
            <h2
              ref={successHeadingRef}
              id={headingId}
              tabIndex={-1}
              className="text-2xl font-bold text-gray-900 mb-2 focus:outline-none"
            >
              {content.successTitle}
            </h2>
            <p className="text-gray-600">
              {content.successBody}
            </p>

            {/* Concrete expectation without an unverified subscriber count. */}
            <p className="text-sm text-gray-500 mt-3">
              Get practical lawn tips for the season ahead.
            </p>

            {/* Value-forward primary CTA into the account-creation leg of the triad. */}
            <a
              href="/auth/login"
              onClick={() =>
                trackEvent('newsletter_success_cta_clicked', {
                  destination: 'auth_login',
                  tool_name: toolName,
                })
              }
              className="mt-5 w-full text-white font-bold tracking-tight px-6 rounded-full transition duration-200 flex items-center justify-center gap-2.5 min-h-[52px] hover:-translate-y-px active:translate-y-0 active:scale-[0.985]"
              style={gradientCtaStyle}
            >
              <span>Create your free account to save your plan</span>
              <ArrowRight className="w-4 h-4 flex-shrink-0" />
            </a>

            <button
              type="button"
              onClick={handleDismiss}
              className="mt-2 w-full rounded-full text-[15px] font-medium text-gray-500 hover:text-gray-700 hover:bg-white/50 active:scale-[0.985] transition py-1.5 px-4 min-h-[44px]"
            >
              Maybe later
            </button>
          </div>
        ) : !confirmedIntent ? (
          // Step 1 — single yes/no micro-commitment. No email field yet.
          <>
            {header}

            <div key="step-intent" ref={modalBodyRef} data-modal-scroll-body className="relative min-h-0 overflow-y-auto overscroll-contain px-5 py-5 sm:px-7 space-y-4 motion-safe:animate-in motion-safe:fade-in-0 duration-200">
              <p className="text-gray-700 text-[15px] text-center">
                {content.microAsk ?? 'Free monthly timing tips for your lawn, sent to your inbox. Want them?'}
              </p>
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  data-modal-initial-focus
                  onClick={handleConfirmIntent}
                  className="w-full flex items-center justify-center gap-2.5 rounded-full px-6 min-h-[52px] text-base font-bold tracking-tight text-white transition duration-200 hover:-translate-y-px active:translate-y-0 active:scale-[0.985]"
                  style={gradientCtaStyle}
                >
                  <Mail className="w-5 h-5" />
                  <span>{content.buttonText}</span>
                </button>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="w-full rounded-full text-[15px] font-medium text-gray-500 hover:text-gray-700 hover:bg-white/50 active:scale-[0.985] transition py-1.5 px-4 min-h-[44px]"
                >
                  No thanks, my lawn is green enough
                </button>
              </div>
            </div>
          </>
        ) : (
          // Step 2 — email form, revealed only after the user said "Yes".
          <>
            {header}

            {/* Content */}
            <div key="step-email" ref={modalBodyRef} data-modal-scroll-body className="relative min-h-0 overflow-y-auto overscroll-contain px-5 py-4 pb-6 sm:px-7 space-y-4 motion-safe:animate-in motion-safe:fade-in-0 duration-200">
              {/* Context bridge references the tool the user just used. Skip
                  generic calculator fallback and passive content-page sources. */}
              {shouldShowContextBridge(toolName) && (
                <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
                  Based on the {getContextBridgeLabel(toolName)} above
                </div>
              )}
              {/* Benefits */}
              <ul className="space-y-2.5">
                {content.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2.5">
                    <CheckCircle className="w-[18px] h-[18px] text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-gray-700 text-[15px] leading-normal">{benefit}</span>
                  </li>
                ))}
              </ul>

              {/* Email form */}
              <form onSubmit={handleSubmit} className="space-y-2.5">
                <div>
                  <label htmlFor={emailInputId} className="sr-only">Email address</label>
                  <input
                    ref={emailInputRef}
                    id={emailInputId}
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => {
                      const value = e.target.value
                      setEmail(value)
                      setError(null) // Clear error when user types
                      setSuggestion(value.includes('@') && value.includes('.') ? detectEmailTypo(value) : null)
                    }}
                    placeholder="Enter your email"
                    required
                    className={`w-full px-4 min-h-[52px] border rounded-[14px] bg-white/75 focus:ring-4 focus:outline-none transition text-base text-gray-900 ${
                      error
                        ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
                        : 'border-white/80 focus:ring-green-200/60 focus:border-green-600/50'
                    }`}
                    style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)' }}
                    disabled={isSubmitting}
                    aria-invalid={!!error}
                    aria-describedby={error ? "email-error" : undefined}
                  />
                  {error && (
                    <div id="email-error" className="mt-2 flex items-start gap-2 text-sm text-red-600" role="alert">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}
                  {suggestion && (
                    <button
                      type="button"
                      onClick={() => {
                        setEmail(suggestion.suggested)
                        setSuggestion(null)
                      }}
                      className="mt-2 text-left text-sm text-green-700 hover:underline"
                    >
                      Did you mean <span className="font-semibold">{suggestion.suggested}</span>?
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full font-bold tracking-tight text-base px-6 rounded-full transition duration-200 flex items-center justify-center gap-2.5 min-h-[52px] ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : email
                        ? 'text-white hover:-translate-y-px active:translate-y-0 active:scale-[0.985]'
                        : 'text-white/90 opacity-80'
                  }`}
                  style={isSubmitting ? undefined : gradientCtaStyle}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full motion-safe:animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      <span>{content.buttonText}</span>
                    </>
                  )}
                </button>
                <p className="text-[13px] text-gray-500 text-center">
                  Once a month. Unsubscribe anytime.
                </p>
              </form>

              {/* Dismiss option */}
              <button
                type="button"
                onClick={handleDismiss}
                className="w-full rounded-full text-[15px] font-medium text-gray-500 hover:text-gray-700 hover:bg-white/50 active:scale-[0.985] transition py-1.5 px-4 min-h-[44px]"
              >
                No thanks, I'd rather guess the timing
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
